import React, { useState, useEffect } from "react";
import { MapPin, Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { useWorkLocations } from "../../contexts/LocationContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

const emptyForm = {
  name: "",
  address: "",
  latitude: "",
  longitude: "",
  geofenceRadius: 100,
  weight: 1,
};

function slugPreview(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const LocationForm = ({ site, onClose, onSaved }) => {
  const { createSite, updateSite, isLoading } = useWorkLocations();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (site) {
      setForm({
        name: site.name || site.label || "",
        address: site.address || "",
        latitude: site.latitude ?? "",
        longitude: site.longitude ?? "",
        geofenceRadius: site.geofenceRadius ?? 100,
        weight: site.weight ?? 1,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [site]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (form.latitude === "" || Number.isNaN(Number(form.latitude))) {
      next.latitude = "Valid latitude is required";
    }
    if (form.longitude === "" || Number.isNaN(Number(form.longitude))) {
      next.longitude = "Valid longitude is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name.trim(),
      address: form.address.trim() || form.name.trim(),
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
      geofenceRadius: Number(form.geofenceRadius) || 100,
      weight: Number(form.weight) || 1,
    };

    const result = site
      ? await updateSite(site._id, payload)
      : await createSite(payload);

    if (result.success) {
      onSaved?.();
      onClose();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-1">Name *</label>
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          className="w-full border border-secondary-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="e.g. Sabarmati Riverfront"
        />
        {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
        {!site && form.name && (
          <p className="text-xs text-secondary-500 mt-1 font-mono">Key: {slugPreview(form.name)}</p>
        )}
        {site?.key && (
          <p className="text-xs text-secondary-500 mt-1 font-mono">Key: {site.key}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-1">Address</label>
        <textarea
          name="address"
          value={form.address}
          onChange={handleChange}
          rows={2}
          className="w-full border border-secondary-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Full address for reports"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">Latitude *</label>
          <input
            name="latitude"
            type="number"
            step="any"
            value={form.latitude}
            onChange={handleChange}
            className="w-full border border-secondary-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="23.0225"
          />
          {errors.latitude && <p className="text-xs text-red-600 mt-1">{errors.latitude}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">Longitude *</label>
          <input
            name="longitude"
            type="number"
            step="any"
            value={form.longitude}
            onChange={handleChange}
            className="w-full border border-secondary-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="72.5714"
          />
          {errors.longitude && <p className="text-xs text-red-600 mt-1">{errors.longitude}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">Geofence (m)</label>
          <input
            name="geofenceRadius"
            type="number"
            min={10}
            max={1000}
            value={form.geofenceRadius}
            onChange={handleChange}
            className="w-full border border-secondary-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">Auto-punch weight</label>
          <input
            name="weight"
            type="number"
            min={1}
            max={10}
            value={form.weight}
            onChange={handleChange}
            className="w-full border border-secondary-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <DialogFooter className="pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? "Saving..." : site ? "Update Location" : "Add Location"}
        </button>
      </DialogFooter>
    </form>
  );
};

const LocationsPage = () => {
  const {
    sites,
    isLoading,
    fetchSites,
    deleteSite,
    seedDefaultSites,
  } = useWorkLocations();
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleEdit = (site) => {
    setEditingSite(site);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingSite(null);
    setShowForm(true);
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Delete "${site.name || site.label}"? This cannot be undone.`)) return;
    await deleteSite(site._id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Work Locations</h1>
          <p className="text-secondary-600">
            Add and manage sites for employee assignment and auto punch-in.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => fetchSites()}
            disabled={isLoading}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {sites.length === 0 && (
            <button
              type="button"
              onClick={() => seedDefaultSites()}
              disabled={isLoading}
              className="btn-secondary"
            >
              Load default sites
            </button>
          )}
          <button type="button" onClick={handleAdd} className="btn-primary flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </button>
        </div>
      </div>

      {sites.length === 0 && !isLoading ? (
        <div className="card p-8 text-center border border-dashed border-secondary-300">
          <MapPin className="h-10 w-10 text-secondary-400 mx-auto mb-3" />
          <p className="text-secondary-700 font-medium">No locations yet</p>
          <p className="text-sm text-secondary-500 mt-1 mb-4">
            Add a site manually or load the five default Panther locations.
          </p>
          <div className="flex justify-center gap-3">
            <button type="button" onClick={() => seedDefaultSites()} className="btn-secondary">
              Load default sites
            </button>
            <button type="button" onClick={handleAdd} className="btn-primary">
              Add Location
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sites.map((loc) => (
            <div key={loc._id || loc.key} className="card p-5 border border-secondary-200">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-secondary-900">{loc.label || loc.name}</h2>
                    <p className="text-sm text-secondary-600 mt-1">{loc.address}</p>
                    <p className="text-xs text-secondary-400 mt-2 font-mono">{loc.key}</p>
                    {loc.latitude != null && loc.longitude != null && (
                      <p className="text-xs text-secondary-400 mt-1">
                        {loc.latitude}, {loc.longitude}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(loc)}
                    className="p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 hover:text-secondary-800"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(loc)}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSite ? "Edit Location" : "Add Location"}</DialogTitle>
          </DialogHeader>
          <LocationForm
            site={editingSite}
            onClose={() => setShowForm(false)}
            onSaved={fetchSites}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsPage;
